import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SavedAddress } from "@/types/address";
import { useAddressBook } from "@/hooks/address/useAddressBook";

export const useAddressActions = () => {
  const { updateExistingAddressAsSaved } = useAddressBook();
  
  const saveAddress = async (values: Omit<SavedAddress, 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save addresses",
          variant: "destructive",
        });
        return;
      }

      // Check if this address already exists in the database (might have been created for a booking)
      const { data: existingAddresses } = await supabase
        .from('address_book')
        .select('id')
        .eq('street', values.street)
        .eq('city', values.city)
        .eq('postal_code', values.postal_code);
      
      // If the address already exists, just update it to be saved
      if (existingAddresses && existingAddresses.length > 0) {
        const addressId = existingAddresses[0].id;
        await updateExistingAddressAsSaved(addressId, values.label || '');
        return;
      }

      // Otherwise insert a new address with is_saved set to true
      const { error } = await supabase
        .from('address_book')
        .insert([{ 
          ...values, 
          user_id: user.id,
          is_saved: true 
        }]);

      if (error) throw error;

      toast({
        title: "Address saved",
        description: "The address has been saved to your address book",
      });
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: "Error",
        description: "Failed to save address. Please try again.",
        variant: "destructive",
      });
    }
  };

  return { saveAddress };
};
